import React, { useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import AnimatedCounter from "../../components/AnimatedCounter";
import { FaArrowRight, FaHeart, FaHandshake, FaUsers } from "react-icons/fa";
import healthOutreach1 from "../../assets/gallery/health/health-outreach1.jpeg";
import donor from "../../assets/gallery/health/donor.jpeg";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function OurSupporters() {
  const supporterCategories = [
    {
      icon: <FaHeart className="text-3xl text-primary-600" />,
      title: "Individual Donors",
      description: "Generous individuals who contribute to our scholarship programs, community development projects, and operational expenses.",
      count: 200,
      suffix: "+"
    },
    {
      icon: <FaHandshake className="text-3xl text-primary-600" />,
      title: "Corporate Partners",
      description: "Forward-thinking companies that support our mission through corporate sponsorships, matching gifts, and in-kind donations.",
      count: 25,
      suffix: "+"
    },
    {
      icon: <FaUsers className="text-3xl text-primary-600" />,
      title: "Community Organizations",
      description: "Fellow diaspora organizations and community groups that collaborate with us on joint initiatives and programs.",
      count: 15,
      suffix: "+"
    }
  ];

  const impactStats = [
    { value: 2, prefix: "$", suffix: "M+", label: "Total Funds Raised" },
    { value: 500, suffix: "+", label: "Lives Impacted" },
    { value: 50, suffix: "+", label: "Scholarships Awarded" },
    { value: 15, suffix: "+", label: "Development Projects" }
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Our Supporters"
        subtitle="Grateful for the generous individuals, organizations, and partners who make our mission possible through their support and contributions."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        heroImage={donor}
      />

      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Thank You to Our Supporters
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our work would not be possible without the generous support of individuals, corporations, and 
                organizations who believe in our mission. Together, we're creating lasting positive change 
                in our community and hometown.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Supporter Categories */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {supporterCategories.map((category, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card text-center"
                  style={{
                    minHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                  }}
                >
                  <div className="flex justify-center mb-6">
                    {category.icon}
                  </div>
                  <div 
                    className="text-4xl font-bold mb-4"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1E40AF',
                      lineHeight: 1.2
                    }}
                  >
                    <AnimatedCounter 
                      end={category.count} 
                      suffix={category.suffix || ""} 
                      duration={2000} 
                    />
                  </div>
                  <h3 
                    className="text-xl font-bold mb-3"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937',
                      lineHeight: 1.3
                    }}
                  >
                    {category.title}
                  </h3>
                  <p 
                    className="text-gray-600 flex-grow"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.6
                    }}
                  >
                    {category.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Impact Through Support
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {impactStats.map((stat, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card text-center"
                  style={{
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <div 
                    className="text-4xl md:text-5xl font-bold mb-3"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1E40AF',
                      lineHeight: 1.2
                    }}
                  >
                    <AnimatedCounter 
                      end={stat.value} 
                      prefix={stat.prefix || ""} 
                      suffix={stat.suffix || ""} 
                      duration={2000} 
                    />
                  </div>
                  <div 
                    className="text-gray-600"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      color: '#374151'
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Join Our Supporters
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Your support makes a real difference. There are many ways to contribute to our mission.
            </p>
            <Link 
              to="/get-involved/donor" 
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Apply Now
              <FaArrowRight />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
